/**
 * CloudinaryImage - Responsive image component with Cloudinary optimization
 *
 * Generates srcset for responsive loading and uses Cloudinary transforms
 * for automatic format selection and quality optimization.
 */

import { cn } from "@wondermaps/ui";

const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || "wondermaps";

interface CloudinaryImageProps {
  publicId: string;
  version?: string;
  alt: string;
  sizes: string; // e.g., "(max-width: 640px) 100vw, 50vw"
  widths?: number[];
  className?: string;
  loading?: "lazy" | "eager";
  aspectRatio?: string;
  quality?: "auto" | number;
  format?: "auto" | "webp" | "avif";
  crop?: "fill" | "fit" | "scale" | "thumb";
  onLoad?: () => void;
  onError?: () => void;
}

function buildUrl(
  publicId: string,
  version?: string,
  options?: {
    width?: number;
    quality?: "auto" | number;
    format?: "auto" | "webp" | "avif";
    aspectRatio?: string;
    crop?: string;
  }
): string {
  const baseUrl = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;
  const transforms: string[] = [];

  if (options?.width) transforms.push(`w_${options.width}`);
  if (options?.quality) {
    transforms.push(`q_${options.quality === "auto" ? "auto" : options.quality}`);
  }
  if (options?.format) {
    transforms.push(`f_${options.format === "auto" ? "auto" : options.format}`);
  }
  if (options?.aspectRatio) {
    transforms.push(`ar_${options.aspectRatio}`);
    transforms.push(`c_${options.crop || "fill"}`);
  } else if (options?.crop) {
    transforms.push(`c_${options.crop}`);
  }

  const transformStr = transforms.length > 0 ? transforms.join(",") : "";
  const versionStr = version ? `v${version}/` : "";

  if (transformStr) {
    return `${baseUrl}/${transformStr}/${versionStr}${publicId}`;
  }

  return `${baseUrl}/${versionStr}${publicId}`;
}

export function CloudinaryImage({
  publicId,
  version,
  alt,
  sizes,
  widths = [320, 640, 960, 1280],
  className,
  loading = "lazy",
  aspectRatio,
  quality = "auto",
  format = "auto",
  crop = "fill",
  onLoad,
  onError,
}: CloudinaryImageProps) {
  // Generate srcset
  const srcSet = widths
    .map((width) => {
      const url = buildUrl(publicId, version, {
        width,
        quality,
        format,
        aspectRatio,
        crop,
      });
      return `${url} ${width}w`;
    })
    .join(", ");

  // Default src (middle size)
  const defaultWidth = widths[Math.floor(widths.length / 2)];
  const src = buildUrl(publicId, version, {
    width: defaultWidth,
    quality,
    format,
    aspectRatio,
    crop,
  });

  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={loading}
      className={cn("object-cover", className)}
      onLoad={onLoad}
      onError={onError}
    />
  );
}

/**
 * Get a single Cloudinary URL for use outside the component
 * Useful for background images or when you need the URL directly
 */
export function getCloudinaryImageUrl(
  publicId: string,
  options?: {
    version?: string;
    width?: number;
    quality?: "auto" | number;
    format?: "auto" | "webp" | "avif";
    aspectRatio?: string;
    crop?: "fill" | "fit" | "scale" | "thumb";
  }
): string {
  return buildUrl(publicId, options?.version, {
    width: options?.width,
    quality: options?.quality ?? "auto",
    format: options?.format ?? "auto",
    aspectRatio: options?.aspectRatio,
    crop: options?.crop,
  });
}
